import { Box, Tooltip, useTheme } from '@mui/material';
import React, { MouseEventHandler, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { isDifferentWebsite, retrievePath } from '../../lib/iris';
import Image from '../Image';

import DetailText from './DetailText';

export enum DetailVariant {
  Default = 'default',
  Error = 'error',
  Bold = 'bold',
  Success = 'success',
  Warning = 'warning',
}

export interface DetailProps {
  className?: string;
  /**
   * Since Detail uses flexbox, you need to place right floating Details
   * detail at the very end of a DetailsBar.
   */
  floatRight?: boolean;
  icon?: string;
  onClick?: MouseEventHandler;
  spin?: boolean;
  text?: ReactNode;
  /** HTML title attribute */
  title?: string | null;
  url?: string;
  variant?: DetailVariant;
}

const iconToImage = (icon: string) => {
  if (icon.includes('/')) {
    return icon;
  }

  return `http://fontawesome.io/icon/${icon}`;
};

const Detail = ({
  className,
  floatRight,
  icon,
  onClick,
  spin,
  text,
  title,
  url,
  variant,
}: DetailProps): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const handleInternalClick = React.useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();

      if (url) {
        navigate(retrievePath(url));
      }
    },
    [url],
  );
  const handleClick = onClick || (url && !isDifferentWebsite(url) ? handleInternalClick : undefined);
  const Element = url ? 'a' : 'div';

  return (
    <Tooltip describeChild title={title ?? ''}>
      <Box
        className={className}
        component={Element}
        data-testid="Detail"
        href={url}
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          // marginRight: '.75rem',
          marginLeft: floatRight ? 'auto' : undefined,
        }}
        target={url && isDifferentWebsite(url) ? '_blank' : undefined}
        onClick={handleClick}
      >
        <DetailText data-test="Detail-DetailText" variant={variant ?? DetailVariant.Default}>
          {icon && (
            <Box
              component={Image}
              spin={spin}
              sx={{
                color: theme.palette.grey.midDark,
                height: '1em',
                marginRight: '.3rem',
                width: '1em',
              }}
              url={iconToImage(icon)}
            />
          )}
          {text}
        </DetailText>
      </Box>
    </Tooltip>
  );
};

export default Detail;
